/**
 * @fileoverview When to plant, on every live plant's card (E13, B136): the server's current or next planting window ("Plant out
 * until early October", "Next: Sow late March to April"), at any stage, as a tinted rounded-square strip (FactStrip: the sprout,
 * the words, a bold chevron). The strip is the door: a tap unfolds every window right under it, above the photo, exactly, with
 * why each falls where it does, and "Hide" shuts it. The old "Planting dates" link and its sheet are gone.
 *
 * A window that is open now gets the "plant now" look (the warm accent tint, accent ink, bold): the server says so with
 * planting.canPlantNow, and the app decides nothing. A "Not sure" plant (nothing recorded) also shows a small "Planted yet?"
 * chip that opens the stage sheet (onPlantedYet).
 *
 * Draws only what the plants brief sends (services/plants/plantingSaid; D40): a plant whose planting is null (archived, or a
 * crop with no dates) shows nothing; with planting, the strip shows when the server sent a line.
 *
 * Where: the plant card (PlantCard), under the bed and the stage tag and above the photo. Whether the tray is open is the
 * card's one-tray-at-a-time state, so the card holds it and gives it here.
 * API: none of its own; the dates come from usePlantsBrief (GET /plants/brief: planting.line, planting.sheet,
 * planting.canPlantNow).
 * Words (en/es): plantingDates.plantedYet, plants.card.tiles.hide; the line's and the windows' are the brief's.
 *
 * @module PlantingDatesLine
 */

import React from 'react';
import { Sprout } from 'lucide-react';
import { TrayHide } from '@/shared/components/ui/TrayHide';
import { FactStrip } from '@/shared/components/ui/FactStrip';
import { Pill } from '@/shared/components/ui/Pill';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { usePlantsBrief } from '@/shared/hooks/usePlantsBrief';
import type { Plant } from '@/shared/types/plant';

/** What the line is given. */
interface PlantingDatesLineProps {
  /** The plant. */
  plant: Plant;
  /** Whether the windows are open under the strip (the card's one open tray). */
  open: boolean;
  /** Opens or shuts them. */
  onToggle: () => void;
  /** A Not sure plant's "Planted yet?": opens the stage sheet. No chip without it. */
  onPlantedYet?: () => void;
}

/**
 * The Not sure chip, the planting strip, and the windows it opens.
 *
 * @param props - PlantingDatesLineProps.
 */
export const PlantingDatesLine: React.FC<PlantingDatesLineProps> = ({ plant, open, onToggle, onPlantedYet }) => {
  const { reactTranslate: t } = useTranslation();
  const { forPlant } = usePlantsBrief();
  const planting = forPlant(plant.plant_obj_id)?.planting ?? null;
  // No dates, no trace.
  if (!planting) return null;

  return (
    <>
      {planting.unsure && onPlantedYet && (
        <Pill tone="outline" onClick={onPlantedYet} className="mb-1.5 text-foreground">{t('plantingDates.plantedYet')}</Pill>
      )}
      {planting.line && (
        <FactStrip icon={<Sprout />} open={open} now={planting.canPlantNow} onClick={onToggle} title={planting.line.title}>
          <span className="min-w-0 truncate">{planting.line.text}</span>
        </FactStrip>
      )}
      {planting.line && open && (
        <div className="mt-1.5 rounded-xl bg-background px-3 py-1.5 text-sm">
          <p className="py-1 text-xs text-muted-foreground">{planting.sheet.note}</p>
          <ul>
            {planting.sheet.windows.map((window) => (
              <li key={window.key} className="space-y-0.5 border-t py-2 first:border-t-0">
                {window.season && <p className="text-xs font-medium text-muted-foreground">{window.season}</p>}
                <p className="font-semibold">{window.text}</p>
                {window.possible && <p className="text-xs text-muted-foreground">{window.possible}</p>}
                {window.reason && <p className="leading-snug">{window.reason}</p>}
              </li>
            ))}
          </ul>
          <TrayHide onClick={onToggle}>
            {t('plants.card.tiles.hide')}
          </TrayHide>
        </div>
      )}
    </>
  );
};
